import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';
import PropTypes from 'prop-types';

import { useIsUserLogged } from '@spotahome/ui-library';
import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';
import ListingContent from '@spotahome/marketplace-common/src/components/ListingContent';

import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';

import OtherRooms from './OtherRooms';
import CanIVisitButton from './CanIVisit';
import { ListingSectionObservable } from './ListingSection';
import ListingEnergyCertificate from './ListingEnergyCertificate';
import ListingDescription from './ListingDescription';
import ListingDetailsFeaturesChips from './ListingDetailsFeaturesChips';

import './ListingOverview.scss';

const ListingOverview = ({
  description = '',
  landlordDescription = '',
  details = '',
  isRoomsAggregator,
  isWholeApartment,
  listingId,
  propertyFeatures,
  propertyFeaturesOldModel,
  roomsInApartment,
  listing,
  locale,
  roomDescription = '',
  hasRoomDescriptionPricing = false
}) => {
  const showDescription = roomDescription && !hasRoomDescriptionPricing;
  const isListingSimplificationHeaderOriginal =
    useSoyuzExperiment('ListingSimplificationHeaderV2').get('behaviour') ===
    'original';

  const isUserLogged = useIsUserLogged();

  const { energyCertificate, emissionsCertificate } = propertyFeatures || {};

  const hasMoreRooms = listing?.otherRooms?.length && !isWholeApartment;

  const renderEveryRoomFeatures = () =>
    roomsInApartment.map((room, i) => (
      <ListingDetailsFeaturesChips
        features={room.features}
        key={room.id}
        title={`${trans('property_show.title.room_in_apartment')} ${i + 1}`}
      />
    ));

  const renderCurrentRoomFeatures = () => {
    // TO-DO: Not sure why we have to extract listing info from a room in apartment.
    // Better move this to GraphQL to expose these features in the ListingType

    const index = roomsInApartment.findIndex(room => room.id === listingId);
    const currentRoom = roomsInApartment[index];

    const getBedroomNumber = (room, roomIndex) => {
      if (room.floorplanReference) {
        return room.floorplanReference;
      }

      return room.bedroomNumber || roomIndex + 1;
    };

    return currentRoom ? (
      <ListingDetailsFeaturesChips
        features={currentRoom.features}
        title={trans('property_show.title.your_room_in_apartment', {
          roomNumber: getBedroomNumber(currentRoom, index)
        })}
      />
    ) : null;
  };

  return (
    <ListingSectionObservable section="overview">
      <div className="listing-overview">
        <ListingTitle role="secondary">
          {trans('listing.overview.property.title')}
        </ListingTitle>

        {isListingSimplificationHeaderOriginal && landlordDescription && (
          <ListingDescription
            className="listing-overview__listing-description"
            title={trans('listing.overview.landlord.description.title')}
            description={landlordDescription}
            titleRole="tertiary"
          />
        )}

        {!isRoomsAggregator && !isWholeApartment && renderCurrentRoomFeatures()}

        {propertyFeaturesOldModel && (
          <ListingDetailsFeaturesChips
            title={trans('property_show.title.property_amenities')}
            features={propertyFeaturesOldModel}
          />
        )}

        {!isRoomsAggregator && isWholeApartment && renderEveryRoomFeatures()}

        {(!!energyCertificate || !!details) && (
          <div className="listing-overview__listing-content">
            <ListingTitle role="tertiary">
              {trans('property_show.title.details')}
            </ListingTitle>
            {(energyCertificate || emissionsCertificate) && (
              <div className="listing-content">
                <ListingEnergyCertificate
                  emissionsCertificate={emissionsCertificate}
                  energyCertificate={energyCertificate}
                />
              </div>
            )}
            {details && <ListingContent content={details} />}
          </div>
        )}

        {isListingSimplificationHeaderOriginal && description && (
          <ListingDescription
            className="listing-overview__listing-description"
            title={trans('listing.overview.description.title')}
            description={description}
            collapsible
            titleRole="secondary"
          />
        )}
        {isListingSimplificationHeaderOriginal && showDescription && (
          <ListingDescription
            className="listing-overview__listing-description"
            title={trans('property_show.title.room_description')}
            description={roomDescription}
            collapsible
            titleRole="tertiary"
          />
        )}

        {!!hasMoreRooms && (
          <div data-slider="other-rooms">
            <ListingTitle role="tertiary">
              {trans('listing.other-rooms.title')}
            </ListingTitle>
            <OtherRooms
              otherRooms={listing.otherRooms}
              mainListing={listing}
              locale={locale}
              isLogged={isUserLogged}
            />
          </div>
        )}
        <div className="listing-overview__can-visit">
          <CanIVisitButton isChecked={listing && listing.checked} />
        </div>
      </div>
    </ListingSectionObservable>
  );
};

ListingOverview.propTypes = {
  listing: PropTypes.shape(ListingShape).isRequired,
  description: PropTypes.string,
  landlordDescription: PropTypes.string,
  details: PropTypes.string,
  isRoomsAggregator: PropTypes.bool.isRequired,
  isWholeApartment: PropTypes.bool.isRequired,
  listingId: PropTypes.string.isRequired,
  propertyFeaturesOldModel: PropTypes.shape({
    [PropTypes.string]: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }).isRequired,
  propertyFeatures: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      value: PropTypes.string
    })
  }).isRequired,
  roomsInApartment: PropTypes.arrayOf(
    PropTypes.shape({
      floorplanReference: PropTypes.number,
      features: PropTypes.shape({}),
      bedroomNumber: PropTypes.number
    })
  ).isRequired,
  locale: PropTypes.string.isRequired,
  roomDescription: PropTypes.string,
  hasRoomDescriptionPricing: PropTypes.bool
};

export default ListingOverview;
